import { Order, CartItem } from '../types';

const ORDER_STORAGE_KEY = 'deviceOrders';
const MAX_STORED_ORDERS = 100;

export interface OrderHistoryService {
  saveOrder: (order: Order) => void;
  getOrders: () => Order[];
  getOrderById: (id: string) => Order | null;
  clearHistory: () => void;
  getOrdersCount: () => number;
  getRecentOrders: (limit?: number) => Order[];
}

/**
 * Service pour gérer l'historique des commandes dans le localStorage
 */
export const orderHistoryService: OrderHistoryService = {
  /**
   * Sauvegarde une commande dans l'historique local
   */
  saveOrder: (order: Order) => {
    try {
      const storedOrders = orderHistoryService.getOrders();
      
      // Vérifier si la commande existe déjà
      const existingOrderIndex = storedOrders.findIndex(o => o.id === order.id);
      
      if (existingOrderIndex !== -1) {
        // Mettre à jour la commande existante
        storedOrders[existingOrderIndex] = {
          ...order,
          updatedAt: new Date().toISOString()
        };
      } else {
        // Ajouter la nouvelle commande au début
        storedOrders.unshift(order);
      }
      
      // Limiter le nombre de commandes stockées
      if (storedOrders.length > MAX_STORED_ORDERS) {
        storedOrders.splice(MAX_STORED_ORDERS);
      }
      
      localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(storedOrders));
      console.log('[OrderHistoryService] Commande sauvegardée:', order.id);
      
      // Déclencher un événement pour notifier les composants
      window.dispatchEvent(new CustomEvent('orderHistoryUpdated', { 
        detail: { order, action: existingOrderIndex !== -1 ? 'updated' : 'added' }
      }));
      
    } catch (error) {
      console.error('[OrderHistoryService] Erreur lors de la sauvegarde:', error);
    }
  },

  /**
   * Récupère toutes les commandes de l'historique
   */
  getOrders: (): Order[] => {
    try {
      const stored = localStorage.getItem(ORDER_STORAGE_KEY);
      if (!stored) return [];
      
      const orders: Order[] = JSON.parse(stored);
      
      // Valider et nettoyer les données si nécessaire
      return orders.filter(order => 
        order && 
        order.id && 
        order.items && 
        Array.isArray(order.items) &&
        typeof order.total === 'number' &&
        order.createdAt
      );
    } catch (error) {
      console.error('[OrderHistoryService] Erreur lors de la récupération:', error);
      return [];
    }
  },

  /**
   * Récupère une commande par son ID
   */
  getOrderById: (id: string): Order | null => {
    const orders = orderHistoryService.getOrders();
    return orders.find(order => order.id === id) || null;
  },

  /**
   * Vide l'historique des commandes
   */
  clearHistory: () => {
    try {
      localStorage.removeItem(ORDER_STORAGE_KEY);
      console.log('[OrderHistoryService] Historique vidé');
      
      // Notifier les composants
      window.dispatchEvent(new CustomEvent('orderHistoryUpdated', { 
        detail: { action: 'cleared' }
      }));
    } catch (error) {
      console.error('[OrderHistoryService] Erreur lors du vidage:', error);
    }
  },

  /**
   * Retourne le nombre total de commandes
   */
  getOrdersCount: (): number => {
    return orderHistoryService.getOrders().length;
  },

  /**
   * Récupère les commandes récentes
   */
  getRecentOrders: (limit: number = 10): Order[] => {
    const orders = orderHistoryService.getOrders();
    return orders.slice(0, limit);
  }
};

/**
 * Utilitaire pour créer une commande à partir des items du panier
 */
export const createOrderFromCart = (
  items: CartItem[],
  total: number,
  options: {
    orderId?: string;
    orderType?: 'takeaway' | 'dine-in';
    paymentMethod?: 'cash' | 'card';
    tableNumber?: string;
    customerName?: string;
    customerId?: string;
  } = {}
): Order => {
  const now = new Date().toISOString();
  
  return {
    id: options.orderId || `order_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    items: items.map(item => ({
      menuItem: {
        id: item.menuItem.id,
        name: item.menuItem.name,
        price: item.menuItem.price,
      },
      quantity: item.quantity,
      customizations: item.customizations,
      uniqueId: item.uniqueId,
    })),
    total,
    status: 'pending',
    createdAt: now,
    orderType: options.orderType,
    paymentMethod: options.paymentMethod,
    tableNumber: options.tableNumber,
    customerName: options.customerName,
    customerId: options.customerId || 'anonymous',
  };
};

/**
 * Utilitaire pour calculer le prix total d'un item avec ses options
 */
export const calculateItemTotal = (item: CartItem): number => {
  const basePrice = item.menuItem.price * item.quantity;
  const optionsPrice = Object.values(item.customizations).reduce((total, options) => {
    return total + options.reduce((sum, option) => sum + (option.price || 0), 0);
  }, 0) * item.quantity;
  
  return basePrice + optionsPrice;
};

/**
 * Utilitaire pour calculer le total du panier
 */
export const calculateCartTotal = (items: CartItem[]): number => {
  return items.reduce((total, item) => total + calculateItemTotal(item), 0);
};